import { config } from '../config';

/**
 * API client for making requests to the backend
 */
class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.apiUrl;
  }

  /**
   * Make a GET request
   */
  async get<T>(path: string, options?: RequestInit): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' });
  }

  /**
   * Make a POST request
   */
  async post<T>(path: string, body?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(path, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });
  }

  /**
   * Make a DELETE request
   */
  async delete<T>(path: string, options?: RequestInit): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE' });
  }

  /**
   * Make a request to the API
   */
  private async request<T>(path: string, options?: RequestInit): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unknown error occurred',
        },
      }));

      // Handle token expiration
      if (response.status === 401 && error.error?.code === 'AUTH_TOKEN_EXPIRED') {
        this.handleTokenExpiration();
      }

      throw new Error(error.error?.message || 'Request failed');
    }

    return response.json();
  }

  /**
   * Handle token expiration by logging out the user
   */
  private handleTokenExpiration() {
    // Clear token from localStorage
    this.clearAuthToken();
    
    // Dispatch custom event to notify the app
    window.dispatchEvent(new CustomEvent('auth:token-expired'));
  }

  /**
   * Set the authorization token for authenticated requests
   */
  setAuthToken(token: string) {
    // Store token in localStorage for persistence
    localStorage.setItem('auth_token', token);
  }

  /**
   * Get the current authorization token
   */
  getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * Clear the authorization token
   */
  clearAuthToken() {
    localStorage.removeItem('auth_token');
  }

  /**
   * Make an authenticated request
   */
  async authenticatedRequest<T>(path: string, options?: RequestInit): Promise<T> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    return this.request<T>(path, {
      ...options,
      headers: {
        ...options?.headers,
        Authorization: `Bearer ${token}`,
      },
    });
  }
}

export const apiClient = new ApiClient();
